import {
  allLanguageSelectOptions,
  allPhonePrefixSelectOptions,
  breederRegionOptions,
  countrySelectOptions,
  genderSelectOptions,
  useRouter,
} from '@application/utils';
import { CountryCode } from '@gql/graphql';
import {
  InfoBox,
  InputDateField,
  InputField,
  InputFileField,
  InputLabel,
  InputRadioField,
  InputSelectField,
  InputWrapper,
  SectionSubtitle,
  FileViewer,
  SectionTitle,
  MtLayoutTitleWrapper,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import { useFormikContext } from 'formik';
import type { FC } from 'react';
import { FaFile } from 'react-icons/fa';
import * as Styled from '../styled';
import type { MtProfileBreederPageProps, MtProfileBreederValues } from '../types';

export const PublicProfile: FC<MtProfileBreederPageProps> = (props) => {
  const { mode, showCertificationFileField = false } = props;
  const { translate } = useTranslation();
  const { language, countryCode } = useRouter();
  const countryOptions = countrySelectOptions(translate);

  const { values } = useFormikContext<MtProfileBreederValues>();
  const currentCountryCode = values?.country ?? countryCode;
  const regionOptions = breederRegionOptions(translate, currentCountryCode);
  const phonePrefixes = allPhonePrefixSelectOptions(translate);

  const showGermanFileFields = currentCountryCode === CountryCode.DE;

  const singleItemArray = new Array(1).fill(null);
  return (
    <>
      <MtLayoutTitleWrapper>
        <SectionTitle>{translate('mt.breeder.onboarding.title.profile')}</SectionTitle>
      </MtLayoutTitleWrapper>

      <InputWrapper split>
        <InputWrapper>
          <InputLabel required>{translate('common.inputs.firstName.label')}</InputLabel>
          <InputField disabled={mode === 'view'} name="firstName" />
        </InputWrapper>

        <InputWrapper>
          <InputLabel required>{translate('common.inputs.lastName.label')}</InputLabel>
          <InputField disabled={mode === 'view'} name="lastName" />
        </InputWrapper>

        <InputWrapper>
          <InputLabel>{translate('common.inputs.birthday.label')}</InputLabel>
          <InputDateField
            disabled={mode === 'view'}
            name="birthday"
            calendarProps={{ defaultView: 'decade', maxDate: new Date() }}
          />
        </InputWrapper>
      </InputWrapper>

      <InputWrapper split last>
        <InputLabel required>{translate('common.inputs.profilePicture.label')}</InputLabel>
        <Styled.ProfilePictureWrapper>
          <InputFileField
            name="profileImage"
            disabled={mode === 'view'}
            accept="image/*, .heic, .heif"
            imageCropperProps={{
              maxCompressedSize: 8 * 1000000,
            }}
          />
        </Styled.ProfilePictureWrapper>
      </InputWrapper>

      <InputWrapper split>
        <InputLabel required>{translate('common.inputs.email.label')}</InputLabel>
        <InputField disabled={mode === 'view'} name="email" type="email" />
      </InputWrapper>

      <InputWrapper split last>
        <InputLabel required>{translate('common.inputs.phone.label')}</InputLabel>

        <InputWrapper inline width="39%" fullWidthBreakpoint="min" noMargin>
          <InputSelectField
            name="phonePrefixCountry"
            options={phonePrefixes}
            disabled={mode === 'view'}
          />
        </InputWrapper>
        <InputWrapper inline width="61%" fullWidthBreakpoint="min" last noMargin>
          <InputField disabled={mode === 'view'} name="phone" type="tel" />
        </InputWrapper>
      </InputWrapper>

      <InputWrapper split>
        <InputLabel required>{translate('common.inputs.spokenLanguages.label')}</InputLabel>
        <InputSelectField<string>
          disabled={mode === 'view'}
          name="spokenLanguages"
          multiple
          dismissOnClick={false}
          options={allLanguageSelectOptions(language, ['en', 'fr', 'nl', 'es', 'pt', 'it', 'de'])}
        />
      </InputWrapper>

      <InputWrapper split>
        <InputLabel>{translate('common.inputs.gender.label')}</InputLabel>
        <InputRadioField
          disabled={mode === 'view'}
          grow
          name="gender"
          options={genderSelectOptions(translate, {
            showIcons: true,
            showText: false,
            showOther: false,
          })}
        />
      </InputWrapper>

      <SectionSubtitle>{translate('glossary.address')}</SectionSubtitle>

      <InputWrapper split>
        <InputLabel required>{translate('common.inputs.city.label')}</InputLabel>
        <InputField disabled={mode === 'view'} name="city" />
      </InputWrapper>

      <InputWrapper split last>
        <InputLabel required>{translate('common.inputs.zipcode.label')}</InputLabel>
        <InputField disabled={mode === 'view'} name="zip" />
      </InputWrapper>

      <InputWrapper split>
        <InputLabel required>{translate('common.inputs.region.label')}</InputLabel>
        <InputSelectField<string>
          disabled={mode === 'view'}
          name="region"
          options={regionOptions}
        />
      </InputWrapper>

      <InputWrapper split>
        <InputLabel required>{translate('common.inputs.country.label')}</InputLabel>
        <InputSelectField<CountryCode>
          disabled={mode === 'view'}
          name="country"
          options={countryOptions}
        />
      </InputWrapper>
      {showGermanFileFields ? (
        <>
          <InputWrapper split>
            <InputLabel>{translate('mt.breeder.onboarding.permissionFiles.label')}</InputLabel>
            <InfoBox>{translate('mt.breeder.onboarding.permissionFiles.warning')}</InfoBox>
            <InputWrapper>
              {mode === 'view' ? (
                <Styled.MultiFileViewer>
                  {(values?.permissionFiles ?? singleItemArray).map((file, index) => (
                    <FileViewer key={`${index}${file}`} file={file} />
                  ))}
                </Styled.MultiFileViewer>
              ) : (
                <Styled.MultipleFilesWrapper>
                  <InputFileField
                    name="permissionFiles"
                    icon={<FaFile />}
                    placeholder={`${translate(
                      'mt.breeder.onboarding.permissionFiles.inputPlaceholder',
                    )}\n Max 8 MB`}
                    accept=".pdf, .doc., image/*, .heic, .heif"
                    isImage={false}
                    multiple
                  />
                </Styled.MultipleFilesWrapper>
              )}
            </InputWrapper>
          </InputWrapper>

          <InputWrapper split>
            <InputLabel>{translate('mt.breeder.onboarding.identityFiles.label')}</InputLabel>
            <InfoBox>{translate('mt.breeder.onboarding.identityFiles.warning')}</InfoBox>

            <InputWrapper>
              {mode === 'view' ? (
                <Styled.MultiFileViewer>
                  {(values?.identityFiles ?? singleItemArray).map((file, index) => (
                    <FileViewer key={`${index}${file}`} file={file} />
                  ))}
                </Styled.MultiFileViewer>
              ) : (
                <Styled.MultipleFilesWrapper>
                  <InputFileField
                    name="identityFiles"
                    icon={<FaFile />}
                    placeholder={`${translate(
                      'mt.breeder.onboarding.identityFiles.inputPlaceholder',
                    )}\n Max 8 MB`}
                    accept=".pdf, .doc., image/*, .heic, .heif"
                    isImage={false}
                    multiple
                  />
                </Styled.MultipleFilesWrapper>
              )}
            </InputWrapper>
          </InputWrapper>
        </>
      ) : (
        <InputWrapper split>
          <InputLabel required={values.country === CountryCode.NL}>
            {translate(`mt.breeder.onboarding.agreementFile.label.${values.country}`)}
          </InputLabel>
          <InputWrapper>
            {[CountryCode.FR].includes(values.country as CountryCode) ? (
              <InfoBox>{translate('mt.breeder.onboarding.agreementFile.warning')}</InfoBox>
            ) : null}
            {[CountryCode.NL, CountryCode.BE].includes(values.country as CountryCode) ? (
              <InfoBox>
                {translate(`mt.breeder.onboarding.agreementFile.requiredText.${values.country}`, {
                  defaultValue: '',
                })}
              </InfoBox>
            ) : (
              ''
            )}
            {mode === 'view' ? (
              <FileViewer file={values?.hkFile} />
            ) : (
              <Styled.SingleFileWrapper>
                <InputFileField
                  name="hkFile"
                  icon={<FaFile />}
                  placeholder={`${translate(
                    'mt.breeder.onboarding.agreementFile.inputPlaceholder',
                  )}\n Max 8 MB`}
                  accept=".pdf, .doc., image/*, .heic, .heif"
                  isImage={false}
                />
              </Styled.SingleFileWrapper>
            )}
          </InputWrapper>
        </InputWrapper>
      )}

      <InputWrapper split>
        <InputLabel>{translate('mt.breeder.onboarding.breedClubFiles.label')}</InputLabel>
        <InfoBox>{translate('mt.breeder.onboarding.breedClubFiles.warning')}</InfoBox>
        <InputWrapper>
          {mode === 'view' ? (
            <Styled.MultiFileViewer>
              {(values?.breedClubFiles ?? singleItemArray).map((file, index) => (
                <FileViewer key={`${index}${file}`} file={file} />
              ))}
            </Styled.MultiFileViewer>
          ) : (
            <Styled.MultipleFilesWrapper>
              <InputFileField
                name="breedClubFiles"
                icon={<FaFile />}
                placeholder={`${translate(
                  'mt.breeder.onboarding.breedClubFiles.inputPlaceholder',
                )}\n Max 8 MB`}
                accept=".pdf, .doc., image/*, .heic, .heif"
                isImage={false}
                multiple
              />
            </Styled.MultipleFilesWrapper>
          )}
        </InputWrapper>
      </InputWrapper>

      {showCertificationFileField && (
        <>
          <InputWrapper split />
          <InputWrapper split>
            <InputLabel>{translate('mt.breeder.onboarding.certificationFile.label')}</InputLabel>
            <InputWrapper>
              <InfoBox>{translate('mt.breeder.onboarding.certificationFile.warning')}</InfoBox>
              {mode === 'view' ? (
                <FileViewer file={values?.certificationFile} />
              ) : (
                <Styled.SingleFileWrapper>
                  <InputFileField
                    name="certificationFile"
                    icon={<FaFile />}
                    placeholder={`${translate(
                      'mt.breeder.onboarding.certificationFile.inputPlaceholder',
                    )}\n Max 8 MB`}
                    accept=".pdf, .doc., image/*, .heic, .heif"
                    isImage={false}
                  />
                </Styled.SingleFileWrapper>
              )}
            </InputWrapper>
          </InputWrapper>
        </>
      )}
    </>
  );
};
